import React from 'react';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { ChildProps } from '@apollo/client/react/hoc';
import { LoggedOutMParticleProfileQuery } from '@reverbdotcom/commons/src/gql/graphql';
import I18n from 'i18n-js';
import { CondensedCategoryConfig, topLevelCategoryForSlug, subCategoryForSlug } from '../category_data';
import classNames from 'classnames';
import { withMpid, MpidProps } from '@reverbdotcom/commons/src/with_mpid';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';

import OverflowingRow from '@reverbdotcom/commons/src/components/overflowing_row';
import { RCGrid, RCImage } from '@reverbdotcom/cadence/components';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import URLHelpers from '../../shared/url_helpers';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { LOGGED_OUT_MPARTICLE_PROFILE_QUERY } from './most_watched_listings';
import { useIsMobileWindowWidth } from '@reverbdotcom/commons/src/is-mobile';

const topLevelCategories = [
  'electric-guitars',
  'keyboards-and-synths',
  'effects-and-pedals',
  'pro-audio',
  'dj-and-lighting-gear',
  'acoustic-guitars',
  'bass-guitars',
  'amps',
  'drums-and-percussion',
  'band-and-orchestra',
];

const subCategoriesForUuid = {
  'electric-guitars': [
    'electric-guitars',
    'bass-guitars',
    'acoustic-guitars',
    'amps',
    'effects-and-pedals',
    'accessories',
  ],
  'acoustic-guitars': [
    'electric-guitars',
    'bass-guitars',
    'acoustic-guitars',
    'amps',
    'effects-and-pedals',
    'accessories',
  ],
  'bass-guitars': [
    'electric-guitars',
    'bass-guitars',
    'acoustic-guitars',
    'amps',
    'effects-and-pedals',
    'accessories',
  ],
  'amps': [
    'electric-guitars',
    'bass-guitars',
    'acoustic-guitars',
    'amps',
    'effects-and-pedals',
    'accessories',
  ],
  'effects-and-pedals': [
    'electric-guitars',
    'bass-guitars',
    'acoustic-guitars',
    'amps',
    'effects-and-pedals',
    'accessories',
  ],
  'pro-audio': [
    'pro-audio/microphones',
    'pro-audio/outboard-gear',
    'pro-audio/speakers',
    'pro-audio/interfaces',
    'pro-audio/mixers',
    'pro-audio/recording',
  ],
  'keyboards-and-synths': [
    'keyboards-and-synths/synths',
    'keyboards-and-synths/midi-controllers',
    'keyboards-and-synths/eurorack',
    'keyboards-and-synths/digital-synths',
    'keyboards-and-synths/samplers',
    'keyboards-and-synths/drum-machines',
  ],
  'drums-and-percussion': [
    'drums-and-percussion/snare',
    'drums-and-percussion/cymbals',
    'drums-and-percussion/tom',
    'drums-and-percussion/full-acoustic-kits',
    'drums-and-percussion/auxiliary-percussion',
    'drums-and-percussion/electronic-drums',
  ],
};

function categoryList(affinityUuid = null) {
  const subCategories = subCategoriesForUuid[affinityUuid];
  if (subCategories?.length) {
    return subCategoriesForUuid[affinityUuid].map(subCategoryForSlug);
  }

  return topLevelCategories.map(topLevelCategoryForSlug);
}

const ROW_ONE_TOTAL = 6;

interface CategoryCardProps {
  categoryItem: CondensedCategoryConfig;
  position: number;
  horizontal?: boolean;
}

type Props = ChildProps<MpidProps, LoggedOutMParticleProfileQuery.Query>;

function CategoryCard(props: CategoryCardProps) {
  const cardClasses = classNames(
    'category-item-redesign',
    { 'category-item-main-row': !props.horizontal },
    { 'category-item-overflow': props.horizontal },
  );
  const categoryText = I18n.t(`discovery.taxonomy.categories.${props.categoryItem.uuid}`);

  return (
    <CoreLink
      to={URLHelpers.categoryPagePath(props.categoryItem.slug)}
      trackName="RedesignedCategoriesRow"
      entityId={props.categoryItem.uuid}
      position={props.position}
      className={cardClasses}
      clickEvent={{
        componentName: 'RedesignedCategoryCard',
        eventName: MParticleEventName.ClickedCategory,
        category: props.categoryItem.slug,
        position: props.position,
        displayStyle: (useIsMobileWindowWidth() || props.horizontal) ? 'horizontal' : 'vertical',
      }}
    >
      <RCImage
        src={props.categoryItem.image}
        aspect={props.horizontal ? 1 : 0.83}
        imageBox="auto"
        borderRadius="lg"
      />
      <div className="category-item__footer">
        <div className="category-item__text">
          {categoryText}
        </div>
      </div>
    </CoreLink>
  );
}

export function HomepageCategories(props: Props) {
  const topAffinities = props.data?.me?.profile?.topMostRecentProductTypes || [];
  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: 'HomepageCategories',
    affinityProductTypeSlug: topAffinities[0],
  }, !props.data.loading);

  return (
    <OverflowingRow title={I18n.t('discovery.homepage.loggedOutNew.browseByCategory')}>
      <RCGrid singleRowOn="mobile">
        {categoryList(topAffinities[0]).map((category, index) => {
          const horizontal = index > (ROW_ONE_TOTAL - 1);
          const cardColSpan = horizontal ? 3 : 2;
          return (
            <RCGrid.Item
              colSpan={{ desktop: cardColSpan, tablet: cardColSpan, mobile: 8 }}
              key={category.slug}
            >
              <CategoryCard
                categoryItem={category}
                position={index}
                horizontal={horizontal}
              />
            </RCGrid.Item>
          );
        })}
      </RCGrid>
    </OverflowingRow>
  );
}

const withQuery = withGraphql<Props, LoggedOutMParticleProfileQuery.Query>(
  LOGGED_OUT_MPARTICLE_PROFILE_QUERY,
  {
    options: (props) => {
      return {
        ssr: false,
        variables: {
          mpid: props.mpid,
        },
      };
    },
    skip: (props) => props.mpid === undefined,
  },
);

export default withMpid<Props>(withQuery(HomepageCategories));
